// packages/web/src/components/Chat/MessageInput.tsx - Mobile Responsive Version

import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

interface MessageInputProps {
  onSendMessage: (content: string) => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  loading = false
}) => {
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || disabled) return;
    
    try {
      await onSendMessage(message.trim());
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        gap: { xs: 0.5, sm: 1 },
        p: { xs: 1, sm: 2 },
        backgroundColor: 'background.paper'
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Type a message..."
        disabled={disabled}
        sx={{
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' },
            padding: { xs: '8px 12px', sm: '10px 14px' }
          }
        }}
      />
      <IconButton
        type="submit"
        color="primary"
        disabled={!message.trim() || disabled || loading}
        sx={{
          alignSelf: 'flex-end',
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 }
        }}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <SendIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        )}
      </IconButton>
    </Box>
  );
};